import { Outlet, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useGetBalanceQuery } from "../../../store/service/clientBetService";
import { APP_BRAND_MARK, APP_BRAND_TAIL } from "../../../utils/brand";
import { ALL_SPORTS_MENU, LobbySportIcon, RACING_SPORTS } from "./lobbySportIcons";
import "./Client.scss";

const IconMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
  </svg>
);

const PROFILE_LINKS = [
  { label: "Account Statement", path: "/play/account-statement" },
  { label: "Current Bets", path: "/play/my-bets" },
  { label: "Casino Results", path: "/play/casino-results" },
  { label: "Set Button Value", path: "/play/set-buttons" },
];

const ClientLayout = () => {
  const nav = useNavigate();
  const token = localStorage.getItem("client-token");
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sportSearch, setSportSearch] = useState("");
  const profileRef = useRef(null);

  useEffect(() => {
    if (!token) {
      nav("/play/login", { replace: true });
    }
  }, [token, nav]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!profileOpen) return;
    const onDoc = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setProfileOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileOpen]);

  const { data: balanceData } = useGetBalanceQuery(undefined, {
    skip: !token,
    pollingInterval: 15000,
  });

  const balance = Number(balanceData?.data?.balance ?? 0);
  const exposure = Number(
    balanceData?.data?.exposure ?? balanceData?.data?.exposer ?? balanceData?.data?.exposureAmount ?? 0
  );
  const username = localStorage.getItem("client-username") || "Player";
  const balanceInt = Math.floor(balance).toLocaleString("en-IN");
  const exposureInt = Math.floor(exposure).toLocaleString("en-IN");

  const q = sportSearch.trim().toLowerCase();
  const racingList = useMemo(
    () => RACING_SPORTS.filter((s) => !q || s.label.toLowerCase().includes(q)),
    [q]
  );
  const sportsList = useMemo(
    () => ALL_SPORTS_MENU.filter((s) => !q || s.label.toLowerCase().includes(q)),
    [q]
  );

  const handleLogout = () => {
    setProfileOpen(false);
    localStorage.removeItem("client-token");
    localStorage.removeItem("client-userId");
    localStorage.removeItem("client-username");
    nav("/play/login");
  };

  const go = (path) => {
    setMenuOpen(false);
    setProfileOpen(false);
    nav(path);
  };

  const openSport = (sport) => {
    setMenuOpen(false);
    if (sport?.sportId) {
      nav(`/play?sport=${sport.sportId}&label=${encodeURIComponent(sport.label)}`);
      return;
    }
    message.info(`${sport.label} jaldi aayega`);
  };

  if (!token) {
    return null;
  }

  return (
    <div className="lotus-app w777-app w777-no-bottom-nav">
      <header className="w777-play-header">
        <button
          type="button"
          className="w777-play-menu"
          aria-label="Open menu"
          onClick={() => {
            setProfileOpen(false);
            setMenuOpen(true);
          }}>
          <IconMenu />
        </button>
        <button type="button" className="w777-play-logo" onClick={() => nav("/play")}>
          <span className="w777-logo-mark">{APP_BRAND_MARK}</span>
          <span className="w777-logo-tail">{APP_BRAND_TAIL}</span>
        </button>
        <div className="w777-play-user" ref={profileRef}>
          <button
            type="button"
            className="w777-play-pts"
            onClick={() => go("/play/account-statement")}
            title="Available | Exposure">
            <span className="w777-pts-label">pts:</span>{" "}
            <span className="w777-pts-bal">{balanceInt}</span>
            <span className="w777-pts-sep"> | </span>
            <span className="w777-pts-exp">{exposureInt}</span>
          </button>
          <div className="w777-play-user-row">
            <button
              type="button"
              className={`w777-play-demo ${profileOpen ? "is-open" : ""}`}
              aria-expanded={profileOpen}
              aria-haspopup="menu"
              onClick={() => setProfileOpen((v) => !v)}>
              <span className="w777-play-avatar" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                  <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4 0-7 2-7 4v1h14v-1c0-2-3-4-7-4z" />
                </svg>
              </span>
              <span className="w777-play-name">{username || "Demo"}</span>
              <em>▾</em>
            </button>
            <button
              type="button"
              className="w777-play-gear"
              onClick={() => go("/play/set-buttons")}
              aria-label="Set button value">
              ⚙
            </button>
          </div>

          {profileOpen ? (
            <div className="w777-profile-dropdown" role="menu">
              {PROFILE_LINKS.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  role="menuitem"
                  onClick={() => go(item.path)}>
                  {item.label}
                </button>
              ))}
              <div className="w777-profile-dropdown-sep" />
              <button type="button" role="menuitem" className="is-logout" onClick={handleLogout}>
                Logout
              </button>
            </div>
          ) : null}
        </div>
      </header>

      <div className="w777-play-ticker">
        <span>Real-Time Odds · Live Sports &amp; Casino Action</span>
        <img src="/Images/lobby/speaker.svg" alt="" />
      </div>

      <main className="lotus-main">
        <Outlet context={{ balance }} />
      </main>

      <div
        className={`lotus-drawer-backdrop w777-sports-backdrop ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />
      <aside
        className={`lotus-drawer w777-sports-drawer ${menuOpen ? "open" : ""}`}
        aria-hidden={!menuOpen}>
        <div className="w777-sports-drawer-top">
          <div className="w777-sports-search">
            <input
              type="search"
              placeholder="search"
              value={sportSearch}
              onChange={(e) => setSportSearch(e.target.value)}
            />
            <span aria-hidden="true">⌕</span>
          </div>
          <button
            type="button"
            className="w777-sports-drawer-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="w777-sports-drawer-body">
          {racingList.length > 0 ? (
            <section className="w777-sports-block">
              <h3>Racing Sports</h3>
              <ul>
                {racingList.map((sport) => (
                  <li key={sport.label}>
                    <button type="button" onClick={() => openSport(sport)}>
                      <LobbySportIcon name={sport.icon} className={`w777-menu-sport-ico tone-${sport.tone || "grey"}`} />
                      <span>{sport.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {sportsList.length > 0 ? (
            <section className="w777-sports-block">
              <h3>All Sports</h3>
              <ul>
                {sportsList.map((sport) => (
                  <li key={sport.label}>
                    <button type="button" onClick={() => openSport(sport)}>
                      <LobbySportIcon name={sport.icon} className={`w777-menu-sport-ico tone-${sport.tone || "grey"}`} />
                      <span>
                        {sport.label}
                        {sport.count != null && sport.count > 0 ? `(${sport.count})` : ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {!racingList.length && !sportsList.length ? (
            <p className="w777-sports-empty">No sports found</p>
          ) : null}
        </div>

        <div className="w777-sports-drawer-foot">
          <button type="button" onClick={() => go("/play/account-statement")}>
            Account Statement
          </button>
          <button type="button" onClick={() => go("/play/my-bets")}>
            Current Bets
          </button>
          <button type="button" onClick={() => go("/play/set-buttons")}>
            Set Button Value
          </button>
          <button type="button" onClick={() => go("/play/deposit")}>
            Deposit
          </button>
          <button type="button" onClick={() => go("/play/withdraw")}>
            Withdraw
          </button>
          <button type="button" className="is-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>
    </div>
  );
};

export default ClientLayout;
